/*
 * Gerenciador de Templates para Boards
 */

import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button, 
  Box, 
  IconButton,
  Tooltip,
  List,
  ListItem,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { 
  getBoardTemplates, 
  createBoardTemplate, 
  updateBoardTemplate, 
  deleteBoardTemplate,
  getBoardTemplateById
} from '../../services/boardTemplate';
import { 
  BoardTemplate, 
  BoardTemplateColumn,
  CreateBoardTemplateRequest 
} from '../../interfaces/boardTemplate';
import { useAuth } from '../../contexts/AuthContext';
import { isSystemAdmin } from '../../utils/userRoles';

import {
  TemplateDialog,
  DialogTitle as StyledDialogTitle,
  DialogContent as StyledDialogContent,
  DialogActions as StyledDialogActions,
  TemplateNameField,
  TemplateDescriptionField,
  ColumnList,
  ColumnItem,
  ColumnNameField,
  DeleteColumnButton,
  AddColumnButton,
  SaveButton,
  CancelButton,
  DeleteTemplateButton,
  TemplateToolbarContainer,
  TemplateToolbar,
  TemplateCardsContainer,
  TemplateCard,
  CardContent,
  ColumnPreview,
  NoTemplatesMessage,
  ActionsContainer
} from './styles';

interface BoardTemplateManagerProps {
  open: boolean;
  onClose: () => void;
  onTemplateSelected?: (templateId: string) => void;
  selectMode?: boolean;
  projectId?: string;
}

export default function BoardTemplateManager({ 
  open, 
  onClose, 
  onTemplateSelected,
  selectMode = false,
  projectId
}: BoardTemplateManagerProps) {
  const [templates, setTemplates] = useState<BoardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para o formulário de edição de template
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<BoardTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [columns, setColumns] = useState<BoardTemplateColumn[]>([]);
  const [newColumnName, setNewColumnName] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Estado para o diálogo de confirmação de exclusão
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<BoardTemplate | null>(null);
  
  // Contexto de autenticação
  const { user } = useAuth();

  // Carregar templates
  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBoardTemplates(projectId);
      setTemplates(data);
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
      setError('Não foi possível carregar os templates. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Lidar com a criação de um novo template
  const handleCreateTemplate = () => {
    setCurrentTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setColumns([]);
    setIsEditDialogOpen(true);
  };

  // Lidar com a edição de um template existente
  const handleEditTemplate = async (template: BoardTemplate) => {
    try {
      setLoadingTemplate(true);
      // Open dialog immediately with loading state
      setIsEditDialogOpen(true);
      
      // Use columns from template if available, otherwise fetch full template
      if (template.columns && template.columns.length > 0) {
        setCurrentTemplate(template);
        setTemplateName(template.name);
        setTemplateDescription(template.description);
        // Ensure columns are sorted by position
        const sortedColumns = [...template.columns].sort((a, b) => a.position - b.position);
        setColumns(sortedColumns);
        setLoadingTemplate(false);
      } else {
        // Fetch complete template data with columns
        const fullTemplate = await getBoardTemplateById(template.id, projectId);
        setCurrentTemplate(fullTemplate);
        setTemplateName(fullTemplate.name);
        setTemplateDescription(fullTemplate.description);
        // Ensure columns are sorted by position and all properties are set correctly
        const sortedColumns = [...(fullTemplate.columns || [])].sort((a, b) => a.position - b.position);
        setColumns(sortedColumns);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes do template:', err);
      setError('Não foi possível carregar os detalhes do template. Por favor, tente novamente.');
      // Close dialog in case of error
      setIsEditDialogOpen(false);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Lidar com a exclusão de um template
  const handleDeleteTemplate = (template: BoardTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  // Confirmar exclusão de template
  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await deleteBoardTemplate(templateToDelete.id, projectId);
      setTemplates(templates.filter(t => t.id !== templateToDelete.id));
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir template:', err);
      setError('Não foi possível excluir o template. Por favor, tente novamente.');
    }
  };

  // Adicionar uma nova coluna
  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    
    const newColumn: BoardTemplateColumn = {
      name: newColumnName,
      position: columns.length + 1
    };
    
    setColumns([...columns, newColumn]);
    setNewColumnName('');
  };

  // Remover uma coluna
  const handleRemoveColumn = (index: number) => {
    const newColumns = [...columns];
    newColumns.splice(index, 1);
    
    // Atualizar as posições
    const updatedColumns = newColumns.map((col, idx) => ({
      ...col,
      position: idx + 1
    }));
    
    setColumns(updatedColumns);
  };

  // Atualizar o nome de uma coluna
  const handleColumnNameChange = (index: number, newName: string) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      name: newName
    };
    setColumns(newColumns);
  };

  // Mover coluna para cima
  const moveColumnUp = (index: number) => {
    if (index === 0) return; // Já está no topo
    
    const newColumns = [...columns];
    // Troca a coluna atual com a anterior
    [newColumns[index-1], newColumns[index]] = [newColumns[index], newColumns[index-1]];
    
    // Atualiza as posições
    const updatedColumns = newColumns.map((col, idx) => ({
      ...col,
      position: idx + 1
    }));
    
    setColumns(updatedColumns);
  };
  
  // Mover coluna para baixo
  const moveColumnDown = (index: number) => {
    if (index === columns.length - 1) return; // Já está no fim
    
    const newColumns = [...columns];
    // Troca a coluna atual com a próxima
    [newColumns[index], newColumns[index+1]] = [newColumns[index+1], newColumns[index]];
    
    // Atualiza as posições
    const updatedColumns = newColumns.map((col, idx) => ({
      ...col,
      position: idx + 1
    }));
    
    setColumns(updatedColumns);
  };

  // Salvar template (criar novo ou atualizar existente)
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    
    try {
      if (currentTemplate) {
        // Atualizar template existente com colunas atualizadas
        const updatedTemplate = await updateBoardTemplate(currentTemplate.id, {
          name: templateName,
          description: templateDescription,
          columns: columns.map(col => ({
            name: col.name,
            position: col.position
          }))
        }, projectId);
        
        setTemplates(templates.map(t => 
          t.id === updatedTemplate.id ? { ...updatedTemplate, columns } : t
        ));
      } else {
        // Criar novo template
        const templateData: CreateBoardTemplateRequest = {
          name: templateName,
          description: templateDescription,
          columns: columns.map(col => ({
            name: col.name,
            position: col.position
          }))
        };
        
        const newTemplate = await createBoardTemplate(templateData, projectId);
        setTemplates([...templates, newTemplate]);
      }
      
      setIsEditDialogOpen(false);
    } catch (err) {
      console.error('Erro ao salvar template:', err);
      setError('Não foi possível salvar o template. Por favor, tente novamente.');
    }
  };

  // Selecionar um template (quando em modo de seleção)
  const handleSelectTemplate = (templateId: string) => {
    if (onTemplateSelected) {
      onTemplateSelected(templateId);
      onClose();
    }
  };

  // Verificar se pode criar templates - deve permitir apenas para administradores do sistema
  const canCreateTemplates = isSystemAdmin(user);
  
  // Verificar se pode editar/excluir templates - baseado em permissão do sistema
  const canManageTemplates = isSystemAdmin(user);
  

  return (
    <TemplateDialog
      open={open}
      onClose={onClose}
      aria-labelledby="board-template-dialog-title"
    >
      <StyledDialogTitle id="board-template-dialog-title">
        {selectMode ? 'Selecionar Template para o Board' : 'Gerenciar Templates de Board'}
      </StyledDialogTitle>
      
      <StyledDialogContent>
        <TemplateToolbarContainer>
          <TemplateToolbar>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Templates disponíveis
            </Typography>
            
            {canCreateTemplates && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTemplate}
                sx={{
                  backgroundColor: '#2196f3',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  },
                }}
              >
                Novo Template
              </Button>
            )}
          </TemplateToolbar>
        </TemplateToolbarContainer>
        
        <TemplateCardsContainer>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Carregando templates...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4, color: 'error.main' }}>
              <Typography>{error}</Typography>
            </Box>
          ) : templates.length === 0 ? (
            <NoTemplatesMessage>
              <Typography variant="body1">
                Nenhum template disponível. Crie um novo template para começar.
              </Typography>
            </NoTemplatesMessage>
          ) : (
            <Box>
            {templates.map((template) => (
              <TemplateCard 
                key={template.id}
                onClick={selectMode ? () => handleSelectTemplate(template.id) : undefined}
              >
                <CardContent>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {template.description || 'Sem descrição'}
                        </Typography>
                      </Box>
                      
                      {!selectMode && (template.canEdit || canManageTemplates) && (
                      <ActionsContainer>
                      <Tooltip title="Editar template">
                      <IconButton 
                      onClick={(e) => {
                      e.stopPropagation();
                      handleEditTemplate(template);
                      }}
                      >
                      <EditIcon />
                      </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir template">
                      <IconButton 
                      color="error"
                      onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTemplate(template);
                      }}
                      >
                      <DeleteIcon />
                      </IconButton>
                      </Tooltip>
                      </ActionsContainer>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ 
                      mr: 1, 
                      minWidth: '90px', 
                      flexShrink: 0,
                      fontWeight: 500
                    }}>
                      Colunas ({template.columns?.length || 0}):
                    </Typography>
                    
                    {template.columns && template.columns.length > 0 ? (
                      <Box sx={{ 
                        flex: 1,
                        overflow: 'hidden'
                      }}>
                        <ColumnPreview>
                          {template.columns.sort((a, b) => a.position - b.position).map((column, index) => (
                            <Box 
                              key={`preview-${index}`} 
                              title={column.name}
                              sx={{
                                backgroundColor: theme => theme.palette.mode === 'dark' ? 
                                  'rgba(66, 153, 225, 0.15)' : 'rgba(66, 153, 225, 0.1)',
                                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 
                                  'rgba(66, 153, 225, 0.3)' : 'rgba(66, 153, 225, 0.2)'}`,
                                color: theme => theme.palette.mode === 'dark' ? 
                                  'rgba(191, 219, 254, 1)' : 'rgba(26, 86, 219, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                height: '24px',
                                fontWeight: 500,
                              }}
                            >
                              {column.name}
                            </Box>
                          ))}
                        </ColumnPreview>
                      </Box>
                    ) : (
                      <Box sx={{ fontStyle: 'italic', color: 'text.secondary', pl: 1 }}>
                        Nenhuma coluna definida
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </TemplateCard>
            ))}
          </Box>
          )}
            </TemplateCardsContainer>
      </StyledDialogContent>
      
      <StyledDialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </StyledDialogActions>

      {/* Diálogo de edição/criação de template */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => !loadingTemplate && setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentTemplate ? 'Editar Template' : 'Novo Template'}
        </DialogTitle>
        
        <DialogContent>
          {loadingTemplate ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Carregando detalhes do template...</Typography>
            </Box>
          ) : (
            <>
              <TemplateNameField
                autoFocus
                margin="dense"
                label="Nome do Template"
                fullWidth
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                required
              />
              
              <TemplateDescriptionField
                margin="dense"
                label="Descrição (opcional)"
                fullWidth
                multiline
                rows={2}
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
              />
              
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Colunas
              </Typography>
              
              {/* Lista de colunas sem drag and drop para simplificar */}
              <ColumnList>
                {columns.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                    Nenhuma coluna adicionada. Adicione uma coluna utilizando o campo abaixo.
                  </Box>
                ) : (
                  columns.map((column, index) => (
                    <ColumnItem key={`col-${index}`}>
                      <Box sx={{ display: 'flex', mr: 2 }}>
                        <Tooltip title="Mover para cima">
                          <IconButton 
                            size="small" 
                            onClick={() => moveColumnUp(index)}
                            disabled={index === 0}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mover para baixo">
                          <IconButton 
                            size="small" 
                            onClick={() => moveColumnDown(index)}
                            disabled={index === columns.length - 1}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <ColumnNameField
                        size="small"
                        value={column.name}
                        onChange={(e) => handleColumnNameChange(index, e.target.value)}
                        placeholder="Nome da coluna"
                        sx={{ flexGrow: 1 }}
                      />
                      
                      <Tooltip title="Remover coluna">
                        <DeleteColumnButton
                          onClick={() => handleRemoveColumn(index)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </DeleteColumnButton>
                      </Tooltip>
                    </ColumnItem>
                  ))
                )}
              </ColumnList>
              
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  size="small"
                  label="Nova coluna"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  sx={{ flexGrow: 1, mr: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddColumn();
                    }
                  }}
                />
                <Button 
                  variant="contained"
                  onClick={handleAddColumn}
                  startIcon={<AddIcon />}
                >
                  Adicionar
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setIsEditDialogOpen(false)} 
            color="inherit"
            disabled={loadingTemplate}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained" 
            color="primary"
            disabled={loadingTemplate || !templateName.trim()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o template "{templateToDelete?.name}"? 
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmDeleteTemplate} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </TemplateDialog>
  );
}
